
import { Badge } from '@/components/ui/badge';
import { CheckSquare, Clock, AlertTriangle, User } from 'lucide-react';
import { useNoteChecklistItems } from '@/hooks/useNoteChecklistItems';

interface NoteChecklistIndicatorProps {
  noteId: string;
  compact?: boolean;
}

export const NoteChecklistIndicator = ({ noteId, compact = false }: NoteChecklistIndicatorProps) => {
  const { checklistItems, isLoadingVirtualCard } = useNoteChecklistItems(noteId);

  if (isLoadingVirtualCard || checklistItems.length === 0) {
    return null;
  }

  const completedItems = checklistItems.filter(item => item.completed).length;
  const totalItems = checklistItems.length;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const overdueItems = checklistItems.filter(item => {
    if (!item.due_date || item.completed) return false;
    return new Date(item.due_date) < new Date();
  }).length;

  const dueSoonItems = checklistItems.filter(item => {
    if (!item.due_date || item.completed) return false;
    const due = new Date(item.due_date);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return due <= tomorrow && due >= new Date();
  }).length;

  const assignedItems = checklistItems.filter(item => item.assigned_to && !item.completed).length;

  const highPriorityItems = checklistItems.filter(item => 
    item.priority === 'high' && !item.completed
  ).length;

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <CheckSquare className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          {completedItems}/{totalItems}
        </span>
        {overdueItems > 0 && (
          <AlertTriangle className="h-3 w-3 text-red-500" />
        )}
        {dueSoonItems > 0 && (
          <Clock className="h-3 w-3 text-yellow-500" />
        )}
        {assignedItems > 0 && (
          <User className="h-3 w-3 text-blue-500" />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="secondary" className="flex items-center gap-1">
        <CheckSquare className="h-3 w-3" />
        {completedItems}/{totalItems} ({completionPercentage}%)
      </Badge>
      
      {overdueItems > 0 && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {overdueItems} overdue
        </Badge>
      )}
      
      {dueSoonItems > 0 && (
        <Badge variant="outline" className="flex items-center gap-1 text-yellow-700 border-yellow-300">
          <Clock className="h-3 w-3" />
          {dueSoonItems} due soon
        </Badge>
      )}

      {assignedItems > 0 && (
        <Badge variant="outline" className="flex items-center gap-1 text-blue-700 border-blue-300">
          <User className="h-3 w-3" />
          {assignedItems} assigned
        </Badge>
      )}

      {highPriorityItems > 0 && (
        <Badge variant="outline" className="flex items-center gap-1 text-red-700 border-red-300">
          <AlertTriangle className="h-3 w-3" />
          {highPriorityItems} high priority
        </Badge>
      )}
    </div>
  );
};
